import {
  Button,
  Checkbox,
  MantineColorsTuple,
  MantineThemeOverride,
  RadioCard,
  TextInput,
  Text,
  Select,
  SegmentedControl,
  Modal,
  Stepper,
  NumberInput,
  PasswordInput,
  Paper,
  Accordion,
  Card,
} from '@mantine/core';
import CheckIcon from '@mui/icons-material/Check';
import classes from '../app.module.css';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import { AreaChart, CompositeChart } from '@mantine/charts';

//Mantine technically wants 10 shades of every color
const repeatColor = (color: string) =>
  Array(10).fill(color) as unknown as MantineColorsTuple;

export const mantineCustomTheme: MantineThemeOverride = {
  fontFamily: 'Carnero',
  primaryColor: 'competenceBlue',
  white: 'clinicalWhite',
  colors: {
    //background-primary, button-primary-text, text & interaction inverted
    clinicalWhite: repeatColor('#FFFFFF'),
    //background-progress-active, button-accent, button-text-&-outline-button, button-tonal-text
    competenceBlue: repeatColor('#0082FA'),
    //button-tonal
    competenceBlueLight: repeatColor('#CCE6FE'),
    //text-&-interaction-accent
    innovationPurple: repeatColor('#BE0096'),
    //text-&-interaction-accent-light
    innovationPurpleLight: repeatColor('#F2CCEA'),
    //
    lifestyleBeige: repeatColor('#B4AA96'),
    //background-tertiary, text-&-interaction-primary
    seriousBlue: repeatColor('#192D50'),
    //text-&-interaction-success
    otherGreen: repeatColor('#178758'),
    //background-secondary
    otherGrey0: repeatColor('#F2F3F4'),
    //button-control-background, button-disabled-border, button-selected-background
    otherGrey10: repeatColor('#E5E6E9'),
    //background-progress-inactive, button-disabled-background
    otherGrey20: repeatColor('#D0D3DB'),
    //button-border-&-control
    otherGrey30: repeatColor('#A5AEBF'),
    //button-disabled-text, text-&-interaction-disabled, text-&-interaction-secondary
    otherGrey60: repeatColor('#647595'),
    //text-&-interaction-error
    otherRed: repeatColor('#DF0000'),
  },
  radius: {
    //corner-basic
    lg: '0.625rem',
    //corner-buttons
    xl: '1.875rem',
  },
  defaultRadius: 'md',
  fontSizes: {
    xs: '12px',
    sm: '14px',
    md: '16px',
    lg: '20px',
    xl: '24px',
  },
  headings: {
    fontFamily: 'Carnero',
    fontWeight: '700',
    textWrap: 'wrap',
    sizes: {
      h1: { fontSize: '32px', fontWeight: '700', lineHeight: '125%' },
      h2: { fontSize: '24px', fontWeight: '700', lineHeight: '138%' },
      h3: { fontSize: '20px', fontWeight: '700', lineHeight: '150%' },
      h4: { fontSize: '16px', fontWeight: '700', lineHeight: '125%' },
      h5: { fontSize: '16px', fontWeight: '350', lineHeight: '125%' },
      h6: { fontSize: '8px', fontWeight: '700', lineHeight: '150%' },
    },
  },
  components: {
    Button: Button.extend({
      styles: { root: { color: 'white', fontSize: 16 } },
      defaultProps: { fz: 'md', size: 'xl', radius: 'xl' },
      classNames: { inner: classes.buttonInner },
    }),
    Checkbox: Checkbox.extend({
      styles: { inner: { marginTop: 7 } },
      defaultProps: { icon: CheckIcon, size: 'md', radius: 'sm' },
      classNames: {
        root: classes.checkbox,
        input: classes.checkboxInput,
        icon: classes.checkboxIcon,
      },
    }),
    Text: Text.extend({
      classNames: { root: classes.text },
    }),
    TextInput: TextInput.extend({
      defaultProps: { size: 'md' },
      classNames: {
        root: classes.inputRoot,
        wrapper: classes.inputWrapper,
        input: classes.input,
        label: classes.inputLabel,
        section: classes.inputSection,
        error: classes.inputError,
      },
    }),
    NumberInput: NumberInput.extend({
      defaultProps: { hideControls: true, size: 'md' },
      classNames: {
        root: classes.inputRoot,
        wrapper: classes.inputWrapper,
        input: classes.input,
        label: classes.inputLabel,
        section: classes.inputSection,
        error: classes.inputError,
      },
    }),
    PasswordInput: PasswordInput.extend({
      defaultProps: { size: 'md' },
      classNames: {
        root: classes.inputRoot,
        wrapper: classes.inputWrapper,
        input: classes.input,
        label: classes.inputLabel,
        section: classes.inputSection,
        error: classes.inputError,
      },
    }),
    RadioCard: RadioCard.extend({
      classNames: { card: classes.radioCard },
    }),
    Select: Select.extend({
      defaultProps: { rightSection: <KeyboardArrowDownOutlinedIcon /> },
      classNames: {
        input: classes.input,
        label: classes.inputLabel,
        dropdown: classes.selectDropdown,
        option: classes.selectOption,
      },
    }),
    SegmentedControl: SegmentedControl.extend({
      defaultProps: { withItemsBorders: false },
      classNames: {
        root: classes.segmentedControl,
        label: classes.segmentedControlLabel,
        indicator: classes.segmentedControlIndicator,
        innerLabel: classes.segmentedControlInnerLabel,
      },
    }),
    Modal: Modal.extend({
      defaultProps: { centered: true },
      classNames: { content: classes.modalContent, title: classes.modalTitle },
    }),
    Stepper: Stepper.extend({
      classNames: {
        root: classes.stepper,
        content: classes.stepperContent,
        steps: classes.stepperSteps,
      },
    }),
    Paper: Paper.extend({
      classNames: {
        root: classes.paper,
      },
    }),
    Card: Card.extend({
      classNames: {
        section: classes.cardSection,
      },
    }),
    Accordion: Accordion.extend({
      defaultProps: {
        disableChevronRotation: true,
        chevron: (
          <CheckIcon
            style={{
              fontSize: 16,
              fill: 'var(--mantine-color-clinicalWhite-0)',
            }}
          />
        ),
      },
      classNames: {
        root: classes.accordion,
        item: classes.accordionItem,
        control: classes.accordionControl,
        content: classes.accordionContent,
        chevron: classes.accordionChevron,
      },
    }),
    AreaChart: AreaChart.extend({
      classNames: {
        grid: classes.areaChartGrid,
      },
    }),
    CompositeChart: CompositeChart.extend({
      classNames: {
        grid: classes.compositeChartGrid,
      },
    }),
  },
};
